import { useEffect, useState } from "react";
import { WorkforceProfileGoalsAndTargetsService } from "../../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileGoalsAndTargetsService";
import { genderList, raceList } from "../../../../../Component/Common/Constants/Lists/DropdownListOptions";
import { Gender } from "../../../../../Component/Model/Enum/BEE123Enums";
import { GridAlignment, GridColDef } from "@mui/x-data-grid";
import { decimalFormatter } from "../../../../../Helpers/DecimalFormatter";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { CustomDialogTitle } from "../../../../Common/Custom/CustomDialogTitle";
import { SmallLoading } from "../../../../../Component/Common/Components/Loading/IntermediateLoading";
import { MDTypography } from "../../../../../Component/Common/Components/MDTypography";
import StyledDataGrid from "../../../../../Component/Common/Components/DataGrid/StyledDataGrid";
import { WorkforceProfileOccupationalLevelTargets } from "../../../../../Component/Model/Dto/EmploymentEquity/WorkforceProfileGoalsAndTargets/WorkforceProfileOccupationalLevelTargets";

interface IProps {
	scorecardId: string;
	appliedSectorName: string;
	handleCancel: () => void;
}

export const AllEAPTargetsModal = (props: IProps) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
	const [tableRows, setTableRows] = useState<any[]>([]);
	const [yearToUse, setYearToUse] = useState<any>();
	const columnHeaders = ['AM', 'CM', 'IM', 'WM', 'Total Male', 'AF', 'CF', 'IF', 'WF', 'Total Female', 'Total'];

	useEffect(() => {
		createTableRows();
	}, []);

	const fetchData = async () => {
		try {
			const year = await WorkforceProfileGoalsAndTargetsService.GetApplicableEAPYear(props.scorecardId);
			setYearToUse(year.data);
			const data = await WorkforceProfileGoalsAndTargetsService.GetAllProvincesEAPTargets(props.scorecardId);
			return data.data;
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const createTableRows = async () => {
		setIsLoading(true);
		let rows: any[] = [];
		const tableData = await fetchData();
		
		tableData?.forEach((section: WorkforceProfileOccupationalLevelTargets.AllProvincesEAPTargets) => {
			rows.push({
				id: section.provinceType,
				description: section.description,
				...mapRowData(section.headcountPercentagePairs)
			});
		});
		setTableRows(rows);
		setIsLoading(false);
	};

	const mapRowData = (rowData: WorkforceProfileOccupationalLevelTargets.HeadcountPercentagePair[]) => {
		let mappedRow = rowData.reduce((acc, { race, gender, percentage }) => {
			const category = `${raceList[race - 1].value.charAt(0)}${genderList[gender - 1].value.charAt(0)}`;
			acc[`${category}`] = percentage;
			return acc;
		}, {} as Record<string, number>);

		mappedRow = {
			...mappedRow,
			[columnHeaders[4]]: rowData.reduce((acc, row) => {
				if (row.gender == Gender.Male) {
					return acc + row.percentage;
				}
				return acc;
			}, 0),
			[columnHeaders[9]]: rowData.reduce((acc, row) => {
				if (row.gender == Gender.Female) {
					return acc + row.percentage;
				}
				return acc;
			}, 0),
			[columnHeaders[10]]: rowData.reduce((acc, row) => acc + row.percentage, 0)
		};

		return mappedRow;
	};

	const columns = (): GridColDef[] => [
		{
			field: 'description',
			headerName: 'Region',
			width: 200,
			disableColumnMenu: true,
			sortable: false,
			headerAlign: 'left',
			headerClassName: 'hideRightSeparator',
			cellClassName: 'left-align-cell',
			renderCell: (params) => {
				const description = params.value || '';
				return description;
			}
		},
		...columnHeaders.flatMap((category: string, index: number) => {
			const isEvenIndex = index % 2 === 0;
			const cellClass = isEvenIndex ? 'greyed-out-cell' : '';

			const fields: GridColDef[] = [
				{
					field: `${category}`,
					headerName: `${category}`,
					minWidth: 60,
					flex: 0.4,
					disableColumnMenu: true,
					sortable: false,
					headerAlign: 'center' as GridAlignment,
					headerClassName: 'hideRightSeparator',
					type: 'number',
					cellClassName: (params) => {
						return cellClass;
					},
					renderCell: (params) => {
						return decimalFormatter(params.value, 2);
					}
				}
			];
			return fields;
		})
	];

	return (
		<Dialog open={true} onClose={props.handleCancel} aria-labelledby='form-dialog-title' maxWidth='lg' fullWidth>
			<CustomDialogTitle id='customized-dialog-title' onClose={props.handleCancel}>
				National and Provincial EAP Percentages
			</CustomDialogTitle>
			<DialogContent>
				{isLoading ? (
					<SmallLoading />
				) : (
					<>
						<MDTypography component='span' variant='body' fontWeight='bold' fontSize='0.9em'>
							Applicable EAP Year:&nbsp;
						</MDTypography>
						<MDTypography component='span' variant='body' fontSize='0.9em'>
							{yearToUse}
						</MDTypography>
						<Grid container direction='row' style={{ width: '100%', overflow: 'hidden' }} mt={1.5} mb={1}>
							<Grid item xs={12} xl={12}>
								<StyledDataGrid
									rows={tableRows}
									columns={columns()}
									disableSelectionOnClick
									hideFooter={true}
									getRowHeight={() => 'auto'}
									autoHeight={true}
								/>
							</Grid>
						</Grid>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}